import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { getInviteByToken, acceptInvite, signUp, signIn } from '../lib/supabase'

export default function AcceptInvite() {
  const { token } = useParams()
  const navigate = useNavigate()
  const [invite, setInvite] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [form, setForm] = useState({ name: '', password: '', confirmPassword: '' })
  const [submitting, setSubmitting] = useState(false)
  const [done, setDone] = useState(false) // email confirmation pending state

  useEffect(() => {
    async function load() {
      const { data, error: err } = await getInviteByToken(token)
      if (err || !data) { setError('This invite link is invalid or has expired. Ask your administrator to send a new one.'); setLoading(false); return }
      if (data.accepted) { setError('This invite has already been used. Sign in directly.'); setLoading(false); return }
      if (new Date(data.expires_at) < new Date()) { setError('This invite link expired after 48 hours. Ask your administrator to resend it.'); setLoading(false); return }
      setInvite(data)
      setLoading(false)
    }
    load()
  }, [token])

  async function handleAccept(e) {
    e.preventDefault()
    if (form.password !== form.confirmPassword) { setError('Passwords do not match.'); return }
    if (form.password.length < 8) { setError('Password must be at least 8 characters.'); return }
    setSubmitting(true); setError('')

    try {
      // ── Try signing up first ──────────────────────────────────
      const { data: authData, error: authErr } = await signUp(invite.email, form.password, {
        full_name: form.name, role: 'lecturer'
      })

      if (authErr) {
        // If the email already exists, try signing in with the provided password instead
        if (authErr.message?.toLowerCase().includes('already registered') || authErr.status === 422) {
          const { data: signInData, error: signInErr } = await signIn(invite.email, form.password)
          if (signInErr) {
            setError('This email already has an account. Wrong password, or sign in at /login first.')
            setSubmitting(false); return
          }
          await acceptInvite(token, signInData.user.id)
          navigate('/lecturer')
          return
        }
        throw authErr
      }

      // ── Sign in first so we have a valid session for RLS ─────
      const { data: signInData, error: signInErr } = await signIn(invite.email, form.password)
      if (signInErr) {
        // Email confirmation is enabled in Supabase
        if (signInErr.message?.toLowerCase().includes('email not confirmed') || signInErr.message?.toLowerCase().includes('not confirmed')) {
          setDone('confirm')
          setSubmitting(false); return
        }
        throw signInErr
      }

      // ── Now authenticated — mark invite accepted ──────────────
      await acceptInvite(token, signInData.user.id)

      navigate('/lecturer')
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.')
    }
    setSubmitting(false)
  }

  if (loading) return <div className="page-center"><span className="spinner" style={{ width: 32, height: 32, borderWidth: 3 }} /></div>

  if (done === 'confirm') return (
    <div className="page-center">
      <div style={{ width: '100%', maxWidth: 440, padding: '0 1rem' }}>
        <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
          <div className="logo" style={{ marginBottom: 4 }}>Campus<span>Flow</span></div>
        </div>
        <div className="card fade-up text-center">
          <div style={{ fontSize: 40, marginBottom: 12 }}>📧</div>
          <div className="card-title mb-2">Check your email</div>
          <div className="card-sub mb-4">
            Your account was created but your Supabase project requires email confirmation.<br /><br />
            Check <strong>{invite?.email}</strong> for a confirmation link, then come back and <span style={{ color: 'var(--blue)', cursor: 'pointer', fontWeight: 600 }} onClick={() => navigate('/login')}>sign in here</span>.
          </div>
          <div className="alert alert-info" style={{ fontSize: 12, textAlign: 'left' }}>
            💡 <strong>Admin tip:</strong> To remove email confirmation, go to Supabase → Authentication → Providers → Email → disable "Confirm email".
          </div>
        </div>
      </div>
    </div>
  )

  if (error && !invite) return (
    <div className="page-center">
      <div style={{ width: '100%', maxWidth: 440, padding: '0 1rem' }}>
        <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
          <div className="logo" style={{ marginBottom: 4 }}>Campus<span>Flow</span></div>
        </div>
        <div className="card text-center">
          <div style={{ fontSize: 36, marginBottom: 8 }}>🔗</div>
          <div className="card-title mb-2">Invalid invite link</div>
          <div className="alert alert-error mb-3">{error}</div>
          <button className="btn btn-secondary" onClick={() => navigate('/login')}>Go to sign in</button>
        </div>
      </div>
    </div>
  )

  return (
    <div className="page-center">
      <div style={{ width: '100%', maxWidth: 440, padding: '0 1rem' }}>
        <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
          <div className="logo" style={{ marginBottom: 4 }}>Campus<span>Flow</span></div>
          <p className="text-sm text-muted">You've been invited as a lecturer</p>
        </div>
        <div className="card fade-up">
          <div style={{ background: 'var(--blue-light)', borderRadius: 'var(--radius)', padding: '1rem', marginBottom: '1.25rem' }}>
            <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--blue)', marginBottom: 2 }}>Invited by</div>
            <div style={{ fontWeight: 700, fontSize: 16 }}>{invite?.organizations?.name}</div>
            <div className="text-sm text-muted mt-1">View-only access to your assigned sessions. No editing.</div>
          </div>
          {error && <div className="alert alert-error mb-3">{error}</div>}
          <form onSubmit={handleAccept}>
            <div className="form-group">
              <label className="field-label">Email address</label>
              <input type="email" value={invite?.email} disabled style={{ background: 'var(--gray-50)', color: 'var(--text-muted)' }} />
            </div>
            <div className="form-group">
              <label className="field-label">Your full name</label>
              <input type="text" required autoFocus placeholder="e.g. Dr. Ama Owusu" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
            </div>
            <div className="form-group">
              <label className="field-label">Set a password</label>
              <input type="password" required placeholder="At least 8 characters" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} />
            </div>
            <div className="form-group">
              <label className="field-label">Confirm password</label>
              <input type="password" required placeholder="Repeat your password" value={form.confirmPassword} onChange={e => setForm({ ...form, confirmPassword: e.target.value })} />
            </div>
            <button type="submit" className="btn btn-primary btn-full mt-3" disabled={submitting || !form.name || form.password.length < 8}>
              {submitting ? <><span className="spinner" /> Setting up your account...</> : 'Accept invite & sign in →'}
            </button>
          </form>
        </div>
      </div>
    </div>
  )
}
